// Job: Web entry: starts Sentry and mounts the marketing or dashboard router depending on the hostname.
// Why: Vite boots from here; every route (guest /g/:slug, dashboard, marketing) is declared once in this file.
import React from 'react';
import ReactDOM from 'react-dom/client';
import { createBrowserRouter, Navigate, RouterProvider, useLocation, useParams } from 'react-router';
import './index.css';
import { initSentry } from './lib/sentry';
import { detectSite } from './host';
import { PreviewOffset } from './features/guidebook/PreviewBanner';

initSentry();

// why: after a deploy, an open tab still asks for the old hashed chunks, which are gone; reload once to pick up the new build
function reloadForNewBuild(): boolean {
  try {
    const last = Number(sessionStorage.getItem('sa-chunk-reload') ?? 0);
    if (Date.now() - last < 10_000) return false; // why: a chunk that's truly missing must not reload forever
    sessionStorage.setItem('sa-chunk-reload', String(Date.now()));
  } catch { /* storage blocked: still reload once */ }
  window.location.reload();
  return true;
}
window.addEventListener('vite:preloadError', e => { if (reloadForNewBuild()) e.preventDefault(); });
const lazyRoute = <T extends React.ComponentType<any>>(load: () => Promise<{ default: T }>) => // eslint-disable-line @typescript-eslint/no-explicit-any -- React.lazy's own signature
  React.lazy(() => load().catch(err => {
    if (reloadForNewBuild()) return new Promise<{ default: T }>(() => {}); // why: hold the Suspense fallback while the page reloads
    throw err;
  }));
// why: every route is lazy so /g/:slug guests don't download dashboard deps (leaflet, supabase, stripe).
const Marketing = lazyRoute(() => import('./features/marketing/Marketing'));
const DashboardLayout = lazyRoute(() => import('./features/dashboard/DashboardLayout'));
const Overview = lazyRoute(() => import('./features/dashboard/Overview'));
const Properties = lazyRoute(() => import('./features/dashboard/Properties'));
const Analytics = lazyRoute(() => import('./features/dashboard/Analytics'));
const Inbox = lazyRoute(() => import('./features/dashboard/Inbox'));
const MapView = lazyRoute(() => import('./features/dashboard/MapView'));
const Editor = lazyRoute(() => import('./features/editor/Editor'));
const PropertySettings = lazyRoute(() => import('./features/settings/PropertySettings'));
const Guidebook = lazyRoute(() => import('./features/guidebook/Guidebook'));
const GuestIdCapture = lazyRoute(() => import('./features/guidebook/GuestIdCapture'));
const GuidebookPreview = lazyRoute(() => import('./features/guidebook/GuidebookPreview'));
const GuidebookDemo = lazyRoute(() => import('./features/guidebook/GuidebookDemo'));
const Stays = lazyRoute(() => import('./features/dashboard/Stays'));
const SettingsGuestPage = lazyRoute(() => import('./features/settings/SettingsGuestPage'));
const SettingsDefaults = lazyRoute(() => import('./features/settings/SettingsDefaults'));
const SettingsWriting = lazyRoute(() => import('./features/settings/SettingsWriting'));
const SettingsStore = lazyRoute(() => import('./features/settings/SettingsStore'));
const SettingsConnections = lazyRoute(() => import('./features/settings/SettingsConnections'));
const SettingsBilling = lazyRoute(() => import('./features/settings/SettingsBilling'));
const Contact = lazyRoute(() => import('./features/dashboard/Contact'));
const Orders = lazyRoute(() => import('./features/dashboard/Orders'));
const SettingsLayout = lazyRoute(() => import('./features/settings/SettingsLayout'));
const SettingsTeam = lazyRoute(() => import('./features/settings/SettingsTeam'));
const InviteAccept = lazyRoute(() => import('./features/auth/InviteAccept'));
const Auth = lazyRoute(() => import('./features/auth/Auth'));
const AuthGuard = lazyRoute(() => import('./features/auth/AuthGuard'));
const Legal = lazyRoute(() => import('./features/marketing/Legal'));
const IntegrationsPage = lazyRoute(() => import('./features/marketing/Integrations'));
const AboutPage = lazyRoute(() => import('./features/marketing/About'));
const HowItWorksPage = lazyRoute(() => import('./features/marketing/HowItWorks'));
const PricingPage = lazyRoute(() => import('./features/marketing/PricingPage'));
const FaqPage = lazyRoute(() => import('./features/marketing/FaqPage'));

const site = detectSite();
const isGuest = typeof window !== 'undefined' && window.location.pathname.startsWith('/g/');
// Suppress the preview banner on the iframed draft-preview path (and any
// other guest-style standalone routes) so the phone-frame in the editor
// renders flush.
const hideBanner = isGuest || (typeof window !== 'undefined' && (window.location.pathname.startsWith('/preview/') || window.location.pathname === '/demo')); // why: /demo is framed as a phone on the home

// why: old /settings/billing links (AI fix hints, Stripe return URLs) keep their ?error= / ?billing= on the way to /billing
function KeepSearch({ to }: { to: string }) {
  return <Navigate to={{ pathname: to, search: useLocation().search }} replace />;
}

const dashboardChildren = [
  { index: true, element: <Overview /> },
  { path: 'properties', element: <Properties /> },
  { path: 'analytics', element: <Analytics /> },
  { path: 'inbox', element: <Inbox /> },
  { path: 'map', element: <MapView /> },
  { path: 'guests', element: <Navigate to="/stays" replace /> }, // why: Guests merged into Stays; keep old links working
  { path: 'stays', element: <Stays /> },
  { path: 'orders', element: <Orders /> },
  { path: 'billing', element: <SettingsBilling /> }, // why: its own page in the avatar menu, not a Settings room
  { path: 'contact', element: <Contact /> },
  {
    path: 'settings',
    element: <SettingsLayout />,
    children: [
      { index: true, element: <Navigate to="guest-page" replace /> }, // why: the setup checklist lives on Overview for new accounts
      { path: 'guest-page', element: <SettingsGuestPage /> },
      { path: 'defaults', element: <SettingsDefaults /> },
      { path: 'writing', element: <SettingsWriting /> },
      { path: 'store', element: <SettingsStore /> },
      { path: 'connections/:provider?', element: <SettingsConnections /> }, // why: the open row is in the URL so fix links land expanded
      { path: 'team', element: <SettingsTeam /> },
      { path: 'billing', element: <KeepSearch to="/billing" /> },
    ],
  },
  { path: 'editor/:slug?', element: <KeyedEditor /> },
  { path: 'properties/:slug', element: <PropertySettings /> },
];

// why: ⌘K can go from one property's editor to another's on the same instance; a fresh one per slug keeps A's queued saves out of B
function KeyedEditor() {
  const { slug } = useParams();
  return <Editor key={slug} />;
}

// why: a chunk that still fails after the one reload would otherwise show React Router's raw error page; a symbol needs no locale, and the guest strings may be the chunk that failed
function RouteError() {
  return (
    <div style={{ minHeight: '100dvh', display: 'grid', placeItems: 'center' }}>
      <button type="button" className="sa-tappable" aria-label="Reload" onClick={() => window.location.reload()}
        style={{ ['--sa-r' as string]: '8px', width: 48, height: 48, background: 'transparent', fontSize: 22, cursor: 'pointer', color: 'inherit' }}>↻</button>
    </div>
  );
}

const router = createBrowserRouter([{ errorElement: <RouteError />, children:
  isGuest
    ? [
        { path: '/g/:slug/id', element: <React.Suspense fallback={<div />}><GuestIdCapture /></React.Suspense> },
        { path: '/g/:slug', element: <React.Suspense fallback={<div />}><Guidebook /></React.Suspense> },
      ]
    : site === 'app'
    ? [
        { path: '/auth', element: <Auth /> },
        // Note: no AuthGuard — InviteAccept handles its own session check
        // because the user may not yet have an org_id (the whole point).
        { path: '/invite/:token', element: <InviteAccept /> },
        // Authed draft preview — renders Guidebook with status-agnostic data
        // fetched via /api/dash/property/:slug/preview-guidebook.
        { path: '/preview/:slug', element: <AuthGuard><React.Suspense fallback={<div />}><GuidebookPreview /></React.Suspense></AuthGuard> },
        { path: '/', element: <AuthGuard><DashboardLayout /></AuthGuard>, children: dashboardChildren },
        // Catch-all on the app site: anything that didn't match (e.g. /dashboard
        // guesses from external links) bounces to Overview instead of hitting
        // React Router's default 404 error boundary.
        { path: '*', element: <Navigate to="/" replace /> },
      ]
    : [
        { path: '/privacy', element: <Legal doc="privacy" /> },
        { path: '/terms', element: <Legal doc="terms" /> },
        { path: '/integrations', element: <IntegrationsPage /> },
        { path: '/about', element: <AboutPage /> },
        { path: '/how-it-works', element: <HowItWorksPage /> },
        { path: '/pricing', element: <PricingPage /> },
        { path: '/faq', element: <FaqPage /> },
        { path: '/demo', element: <GuidebookDemo /> }, // why: the home's phone frame; same origin so the home can drive its hash
        { path: '*', element: <Marketing /> },
      ]
}]);

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    {/* why: one shared fallback for all lazy routes; router navigations run in startTransition so old UI stays up */}
    <React.Suspense fallback={<div />}>
      {hideBanner ? (
        <RouterProvider router={router} />
      ) : (
        <PreviewOffset>
          <RouterProvider router={router} />
        </PreviewOffset>
      )}
    </React.Suspense>
  </React.StrictMode>
);
